// pages/GestionDesMots.jsx
// Page de gestion des mots avec recherche

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, Search, ArrowLeft } from 'lucide-react';
import GestionMots from '../components/GestionMots';
import './GestionDesMots.css';

export default function GestionDesMots() {
  const location = useLocation();
  const [showResults, setShowResults] = useState(false);
  const [langue, setLangue] = useState('fr');
  const [debutMot, setDebutMot] = useState('');
  const [mots, setMots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextes, setContextes] = useState([]);

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowResults(false);
    }
  }, [location.state]);

  // Charger les contextes au montage
  useEffect(() => {
    loadContextes();
  }, []);

  // Charger la liste des contextes prédéfinis
  const loadContextes = async () => {
    try {
      const response = await fetch('/api/contextes');
      const data = await response.json();

      if (data.success) {
        const contextesTries = [...(data.contextes || [])].sort((a, b) => {
          if (a.value === '') return -1;
          if (b.value === '') return 1;
          return a.label.localeCompare(b.label, 'fr');
        });
        setContextes(contextesTries);
      }
    } catch (err) {
      console.error('Erreur chargement contextes:', err);
    }
  };

  // Rechercher les mots
  const handleRechercher = async () => {
    if (!debutMot.trim() || debutMot.trim().length < 2) {
      alert('❌ Veuillez saisir au moins 2 caractères');
      return;
    }

    setLoading(true);
    setError(null);
    setShowResults(true);

    try {
      const response = await fetch(`/api/mots/recherche?langue=${langue}&debut=${encodeURIComponent(debutMot.trim())}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la recherche');
      }

      setMots(data.mots || []);
    } catch (err) {
      console.error('Erreur recherche mots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les résultats
  const handleReload = () => {
    handleRechercher();
  };

  // Retour à l'écran de recherche
  const handleRetourRecherche = () => {
    setShowResults(false);
    setMots([]);
    setError(null);
  };

  // Gérer la touche Entrée dans le champ de recherche
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRechercher();
    }
  };

  return (
    <div className="gestion-mots-page">
      {/* Écran de recherche */}
      {!showResults && (
        <div className="recherche-screen">
          <div className="page-header">
            <h1>
              <BookOpen size={32} color="#4F46E5" />
              Gestion des Mots
            </h1>
            <p className="page-description">
              Recherchez et gérez les mots dans le vocabulaire
            </p>
          </div>

          <div className="recherche-form">
            <div className="form-group">
              <label htmlFor="langue-select">🌍 Langue</label>
              <select
                id="langue-select"
                value={langue}
                onChange={(e) => setLangue(e.target.value)}
                className="form-select"
              >
                <option value="fr">🇫🇷 Français</option>
                <option value="es">🇪🇸 Espagnol</option>
                <option value="en">🇬🇧 Anglais</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="debut-input">🔍 Début du mot (min. 2 caractères)</label>
              <input
                id="debut-input"
                type="text"
                value={debutMot}
                onChange={(e) => setDebutMot(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ex: chat, mai..."
                className="form-input"
                autoFocus
              />
              <p className="form-hint">
                La recherche est insensible aux majuscules et aux accents
              </p>
            </div>

            <button
              onClick={handleRechercher}
              disabled={!debutMot.trim() || debutMot.trim().length < 2}
              className="btn-rechercher"
            >
              <Search size={20} />
              Rechercher
            </button>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <p style={{ color: '#6B7280', marginBottom: '10px' }}>ou</p>
              <button
                onClick={() => {
                  const mot = prompt('Mot au singulier:');
                  if (!mot) return;

                  const pluriel = prompt('Pluriel (optionnel):') || '';
                  const contexte = prompt('Contexte (optionnel, ex: animal, aliment, lieu...):') || '';

                  fetch('/api/mots', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      langue,
                      motSingulier: mot.trim(),
                      motPluriel: pluriel.trim() || null,
                      contexte: contexte.trim() || null,
                      type: 'nom',
                      genre: '',
                    }),
                  })
                    .then(res => res.json())
                    .then(data => {
                      if (data.success) {
                        alert('✅ Mot créé avec succès!');
                        setDebutMot(mot.substring(0, 2));
                        handleRechercher();
                      } else {
                        alert('❌ ' + (data.error || 'Erreur'));
                      }
                    })
                    .catch(err => alert('❌ ' + err.message));
                }}
                className="btn-create"
                style={{
                  padding: '10px 20px',
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                ➕ Créer un nouveau mot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Écran de résultats */}
      {showResults && (
        <div className="resultats-screen">
          <div className="page-header">
            <div className="header-with-back">
              <button onClick={handleRetourRecherche} className="btn-back">
                <ArrowLeft size={20} />
                Nouvelle recherche
              </button>
              <h1>
                <BookOpen size={32} color="#4F46E5" />
                Résultats : mots commençant par "{debutMot}"
              </h1>
            </div>
            <p className="page-description">
              Langue : {langue === 'fr' ? '🇫🇷 Français' : langue === 'es' ? '🇪🇸 Espagnol' : '🇬🇧 Anglais'}
            </p>
          </div>

          <GestionMots
            mots={mots}
            loading={loading}
            error={error}
            onReload={handleReload}
            showValiderButton={false}
            contextes={contextes}
            emptyMessage="Aucun mot trouvé pour cette recherche"
            emptyHint="Essayez avec d'autres lettres ou une autre langue"
          />
        </div>
      )}
    </div>
  );
}
